import React, {
    ChangeEvent,
    FormEventHandler,
    KeyboardEventHandler,
    useEffect,
    useState,
} from 'react'
import { Stripe, StripeCardElement } from '@stripe/stripe-js'

import { classNames } from 'utils/dom-helpers'
import { BookingForm } from 'types/booking'
import { PaymentMethod } from 'types/payment'
import CheckoutForm from './CheckoutForm'
import PaymentOptions from './PaymentOptions'
import Translation from 'components/Translation'
import { useRouter } from 'next/router'
import { MembershipSelect } from './MembershipComponent'
import {
    ClientMembershipItem,
    MembershipClientOutput,
} from 'types/client-membership'
import useDebounce from 'utils/useDebounceHook'
import { useTenantContext } from 'context/TenantContext'
import { useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { checkCouponAvailability } from 'services/coupons'
import { useAppointmentContext } from 'context/appointmentPurchaseContext/AppointmentPurchaseContainer'
import { validateEmail } from 'utils/string-helper'
import { DiscountType } from 'types/discount'
import { SHOW_MEMBERSHIPS } from 'components/Modals/CustomBookingPage/GeneralForm'

export interface PaymentFormProps {
    booking_data: BookingForm
    classnames?: string
    label?: string
    availability_id?: string
    setBookingData(d: BookingForm): void
    setCard(card: StripeCardElement): void
    handleSubmit: KeyboardEventHandler | FormEventHandler
    getLocalStorageValue?: () => void
}

export function PaymentForm({
    booking_data,
    classnames,
    availability_id,
    setBookingData,
    setCard,
    handleSubmit,
    getLocalStorageValue,
}: PaymentFormProps) {
    const router = useRouter()
    const { tenant, db_settings } = useTenantContext()
    const {
        setPaymentMethod,
        setIsValidCoupon,
        setCouponInfo,
        setCouponCode: setCouponCodeInContext,
        setIsValidMembership,
        paymentMethod,
    } = useAppointmentContext()
    const [stripe, setStripe] = useState<Stripe>()
    const [coupon_code, setCouponCode] = useState<string>(
        (router.query.coupon as string) || ''
    )
    const [payment_method, setPayment] = useState<PaymentMethod>(
        !!coupon_code
            ? PaymentMethod.COUPON
            : booking_data.payment_method || PaymentMethod.MEMBER
    )
    const [has_coupon, showCouponInput] = useState<boolean>(!!coupon_code)
    const [is_valid_coupon, setCouponValidity] = useState<boolean>()
    const [membership_options, setMembershipOptions] = useState<
        ClientMembershipItem[]
    >([])

    const serviceId =
        (router?.query?.s as string) || booking_data?.service_id
    const location_id = router.query.l as string
    const query_params: Record<string, string> = {
        tenantId: tenant?.id as string,
        serviceId: serviceId as string,
        locationId: location_id
    }

    query_params.clientEmail = useDebounce(
        booking_data.email as string,
        500
    )

    if (availability_id) {
        query_params.groupClassAvailabilityId = availability_id
    }

    query_params.couponCode = useDebounce(coupon_code, 500)
    const { is_loading: is_service_loading, data } = useFetch(
        `/v1/memberships/clients/by-service?${Object.keys(query_params)
            .map((key: string) => {
                if (query_params[key] != undefined) {
                    return `${key}=${encodeURIComponent(query_params[key])}`
                }
            })
            .join('&')}`,
        FetchMethods.GET,
        validateEmail(query_params.clientEmail) &&
        payment_method === PaymentMethod.MEMBER
    )

    const { is_loading: is_checking_code, data: coupon_xhr } = useFetch(
        `/v1/coupons/check-coupon-availability-with-email?${Object.keys(
            query_params
        )
            .map((key: string) => {
                if (query_params[key] != undefined) {
                    if (key == 'clientEmail') {
                        //TODO: remove after BE standardizes the param names
                        return `email=${encodeURIComponent(
                            query_params[key]
                        )}`
                    }
                    return `${key}=${encodeURIComponent(query_params[key])}`
                }
            })
            .join('&')}`,
        FetchMethods.GET,
        !!query_params.couponCode &&
        validateEmail(query_params.clientEmail) &&
        payment_method === PaymentMethod.COUPON
    )

    // useEffect(() => {
    //     const service_id = booking_data?.service_id
    //     if (query_params.clientEmail && query_params.code === coupon_code && query_params.code && service_id) {
    //         checkCouponAvailability(
    //             query_params.code,
    //             booking_data?.service_id as string,
    //             query_params.clientEmail
    //         ).then((res) => {
    //             if (res?.error) {
    //                 setCouponValidity(false)
    //                 setIsValidCoupon(false)

    //                 localStorage.setItem('is_valid_coupon', 'false')
    //             } else if (res.code === coupon_code) {
    //                 setCouponValidity(true)
    //                 setIsValidCoupon(true)
    //                 setBookingData({
    //                     ...booking_data,
    //                     coupon_code,
    //                 })
    //                 setCouponInfo((prev: any) => res)
    //                 localStorage.setItem('is_valid_coupon', 'true')
    //             }
    //         })
    //     }
    // }, [query_params])

    useEffect(() => {
        if (!is_checking_code && Object.keys(coupon_xhr).length > 0) {
            const { discountValue, discountType } = coupon_xhr
            if (
                discountType === DiscountType.PERCENT &&
                discountValue === 100
            ) {
                setIsValidCoupon(true)
                setCouponValidity(true)
                setCouponInfo(coupon_xhr)
                setBookingData({
                    ...booking_data,
                    coupon_code,
                })
            } else {
                setIsValidCoupon(false)
                setCouponValidity(false)
                setBookingData({
                    ...booking_data,
                    coupon_code: undefined,
                })
            }
            setCouponCodeInContext(coupon_code)
        }
    }, [is_checking_code, coupon_xhr])

    useEffect(() => {
        const found =
            db_settings?.content?.find(
                (item: any) => item.name === SHOW_MEMBERSHIPS
            )?.value === 'false'
        if (!is_service_loading && !!data && !!data.length) {
            if (!data || data.length === 0 || found) {
                setMembershipOptions([])
            } else {
                const memberships = data.map(
                    (mc: MembershipClientOutput) => {
                        const { membership, membershipClient } = mc
                        const client_membership: ClientMembershipItem = {
                            id: membershipClient.id,
                            membership: {
                                id: membership.id as string,
                                name: membership.name,
                                img: membership?.imageUrl as string,
                            },
                            client: {
                                id: membershipClient.clientId,
                                used: {
                                    services:
                                        membershipClient.usedServiceCount,
                                    minutes: membershipClient.usedMinutes,
                                    amount: membershipClient.usedAmount,
                                },
                                starts_at: membershipClient.startsAt,
                                expires_at: membershipClient.expiresAt,
                                renews_at: membershipClient.renewsAt,
                            },
                            service_count: mc.availableServiceCount,
                            minute_count: mc.availableMinutes,
                            amount: mc.availableAmount,
                            unlimited: mc.redemptionUnlimited,
                            entitled: mc.entitled,
                            not_entitled_reason: mc.notEntitledReason,
                            content_tag: mc.contentTag,
                            member_price: mc.memberPrice,
                            member_price_type: mc.memberPriceType,
                            service_price: mc.servicePrice,
                            service_full_price: mc.serviceFullPrice,
                            discount_amount: mc.discountAmount,
                        }
                        return client_membership
                    }
                )
                setMembershipOptions(memberships)
            }
        } else {
            setMembershipOptions([])
            const { membership, ...bookings } = booking_data
            setBookingData(bookings)
            setIsValidMembership(false)
        }
    }, [is_service_loading, data, booking_data.email, db_settings])

    useEffect(() => {
        if (payment_method === PaymentMethod.COUPON) {
            showCouponInput(true)
        } else {
            showCouponInput(false)
            setCouponCode('')
        }
    }, [payment_method])

    const handleCouponChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        localStorage.setItem('coupon_code', value)
        setCouponCodeInContext(value)
        checkCouponAvailability({
            coupon_code: value,
            service_id: booking_data?.service_id,
        }).then((res) => {
            if (res?.error) {
                setCouponValidity(false)
                setIsValidCoupon(false)

                localStorage.setItem('is_valid_coupon', 'false')
            } else if (res.code === value) {
                setCouponValidity(true)
                setIsValidCoupon(true)
                setBookingData({
                    ...booking_data,
                    coupon_code: e.target.value,
                })
                setCouponInfo((prev: any) => res)
                localStorage.setItem('is_valid_coupon', 'true')
            }
        })
    }

    useEffect(() => {
        setCouponCode('')
        localStorage.setItem('coupon_code', '')
    }, [payment_method])

    useEffect(() => {
        if (router?.query?.coupon) {
            setPaymentMethod(PaymentMethod.COUPON)
            setCouponCode(router?.query?.coupon as string)
        }
    }, [router.query])

    useEffect(() => {
        if (router.pathname.includes('memberships')) {
            setPayment(PaymentMethod.CARD)
        }
    }, [router.pathname])

    const activeButton = () => {
        if (getLocalStorageValue) {
            getLocalStorageValue()
        }
    }

    useEffect(() => {
        setPaymentMethod(payment_method)
    }, [payment_method])

    return (
        <div
            className={classNames(
                'flex flex-col w-full gap-4',
                classnames || ''
            )}
        >
            <div>
                <PaymentOptions
                    order_type={booking_data.order_type}
                    active_button={payment_method}
                    onToggle={(method: PaymentMethod) => {
                        delete booking_data.membership
                        setBookingData(booking_data)
                        setPayment(method)
                        setPaymentMethod(method as string)
                        setCouponCode('')
                        setIsValidCoupon(false)
                        localStorage.setItem(
                            'payment_method',
                            JSON.stringify(method)
                        )
                    }}
                    activeButton={activeButton}
                />

                {/* Customer chose membership */}
                {payment_method === PaymentMethod.MEMBER &&
                    <>
                        {
                            !!membership_options.length ? (
                                <div className="xl:w-1/2 mt-5">
                                    <MembershipSelect
                                        options={membership_options}
                                        onSelect={(
                                            membership: ClientMembershipItem
                                        ) => {
                                            if (membership) {
                                                setIsValidMembership(true)
                                            }
                                            setBookingData({
                                                ...booking_data,
                                                membership,
                                            })
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className='flex flex-col w-full gap-y-4 mb-4'>
                                    <div className='flex flex-row w-full gap-x-3.5 items-center bg-neutral-50 border border-gray-four border-dashed py-3 px-5 rounded-lg'>
                                        <i className='feather-shopping-bag text-xl text-gray-2' />
                                        <div className='flex flex-col'>
                                            <Translation
                                                content_key='booking_page.no_available_membership'
                                                render_as='h4'
                                                className='text-gray-one font-sm font-medium'
                                            />
                                            <Translation
                                                content_key='booking.select_other_payment_method'
                                                render_as='p'
                                                className='text-zinc-500 font-xs font-[450]'
                                            />
                                        </div>
                                    </div>
                                    <div className='flex flex-row flex-wrap text-sm font-[450] text-dark'>
                                        <Translation
                                            content_key='booking.purchase_membership_for_class'
                                        />
                                        &nbsp;
                                        <button
                                            className='font-bold text-secondary-blue'
                                            onClick={() => {
                                                router.push({
                                                    pathname: "/book/memberships",
                                                    query: router.query
                                                })
                                            }}
                                        >
                                            <Translation
                                                content_key='booking.go_to_memberships'
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}
                    </>
                }

                {/* Card method */}
                {payment_method === PaymentMethod.CARD ? (
                    <CheckoutForm
                        booking_data={booking_data}
                        setBookingData={setBookingData}
                        handleSubmit={handleSubmit}
                        setCard={(card: StripeCardElement) => {
                            setCard(card)
                        }}
                        setStripe={setStripe}
                    />
                ) : (
                    <></>
                )}

                {/* Coupon Method */}
                <fieldset
                    className={classNames(
                        has_coupon ? '' : 'hidden',
                        'w-72 mb-4'
                    )}
                >
                    <label
                        className="w-full text-gray-800 relative"
                        htmlFor="coupon_code"
                    >
                        <p className="leading-loose">
                            <Translation
                                content_key="coupon_code"
                                render_as="span"
                            />
                            {!validateEmail(query_params.clientEmail) ? (
                                <>
                                    <sup className="text-slate-500">
                                        *
                                        <Translation content_key="valid_email_required" />
                                    </sup>
                                </>
                            ) : (
                                ''
                            )}
                        </p>
                        <div className="relative">
                            <input
                                className={classNames(
                                    'w-full rounded-lg border border-gray-300 placeholder-gray-300 disabled:bg-gray-100/80 disabled:border-gray-100',
                                    is_valid_coupon &&
                                        coupon_xhr &&
                                        !is_checking_code &&
                                        coupon_code
                                        ? 'bg-green-100/60 text-green-800 font-semibold'
                                        : ''
                                )}
                                type="text"
                                id="coupon_code"
                                name="coupon_code"
                                autoComplete={'false'}
                                defaultValue={coupon_code}
                                disabled={
                                    !validateEmail(query_params.clientEmail)
                                }
                                onChange={(e) => {
                                    setCouponCode(e.target.value)
                                }}
                            />

                            {Object.keys(coupon_xhr).length > 0 &&
                                !is_checking_code &&
                                coupon_code && (
                                    <i
                                        className={classNames(
                                            is_valid_coupon
                                                ? 'feather-check-circle text-green-500'
                                                : 'feather-x-circle text-red-500',
                                            ' absolute right-3 top-1.5 text-xl'
                                        )}
                                    />
                                )}
                        </div>
                    </label>
                </fieldset>
            </div>
        </div>
    )
}

export default PaymentForm
